import { join, dirname } from 'path';
import { ProjectInfo, Framework } from './types';

export class FileOperations {
  /**
   * Detect framework from package.json and project structure
   */
  static async detectFramework(rootDir: string): Promise<Framework> {
    const packageJsonPath = join(rootDir, 'package.json');
    const packageJson = await this.readFile(packageJsonPath);
    
    if (!packageJson) {
      return 'unknown';
    }

    const pkg = JSON.parse(packageJson);
    const dependencies = { ...pkg.dependencies, ...pkg.devDependencies };

    // Next.js detection
    if (dependencies.next) {
      return 'nextjs';
    }

    // React detection (with build tool differentiation)
    if (dependencies.react) {
      const { existsSync } = await import('fs');
      if (dependencies.vite || existsSync(join(rootDir, 'vite.config.js')) || existsSync(join(rootDir, 'vite.config.ts'))) {
        return 'react-vite';
      }
      if (dependencies['react-scripts'] || existsSync(join(rootDir, 'public/index.html'))) {
        return 'react-cra';
      }
      return 'react-vite'; // Default React to Vite
    }

    // Vue detection
    if (dependencies.vue || dependencies['@vue/cli-service']) {
      return 'vue';
    }

    // Angular detection
    if (dependencies['@angular/core'] || dependencies['@angular/cli']) {
      return 'angular';
    }

    // Svelte detection
    if (dependencies.svelte || dependencies['svelte-kit']) {
      return 'svelte';
    }

    // Backend framework detection
    if (dependencies.express) {
      return 'express';
    }
    if (dependencies.fastify) {
      return 'fastify';
    }

    // Node.js detection
    if (pkg.type === 'module' || dependencies.node) {
      const { existsSync } = await import('fs');
      if (existsSync(join(rootDir, 'index.js')) || existsSync(join(rootDir, 'index.ts'))) {
        return 'nodejs';
      }
    }

    return 'unknown';
  }

  /**
   * Safely read a file with error handling (obfuscation-safe)
   */
  static async readFile(filePath: string): Promise<string | null> {
    try {
      const { readFileSync } = await import('fs');
      return readFileSync(filePath, 'utf-8');
    } catch (error) {
      console.warn(`Failed to read file ${filePath}:`, error);
      return null;
    }
  }

  /**
   * Safely write a file with directory creation
   */
  static async writeFile(filePath: string, content: string): Promise<boolean> {
    try {
      const { writeFileSync, existsSync, mkdirSync } = await import('fs');
      // Ensure directory exists
      const dir = dirname(filePath);
      if (!existsSync(dir)) {
        mkdirSync(dir, { recursive: true });
      }
      
      writeFileSync(filePath, content, 'utf-8');
      return true;
    } catch (error) {
      console.error(`Failed to write file ${filePath}:`, error);
      return false;
    }
  }

  /**
   * Check if file exists
   */
  static async fileExists(filePath: string): Promise<boolean> {
    const { existsSync } = await import('fs');
    return existsSync(filePath);
  }

  /**
   * Create backup of existing file
   */
  static async createBackup(filePath: string): Promise<string | null> {
    if (!(await this.fileExists(filePath))) {
      return null;
    }

    const backupPath = `${filePath}.backup.${Date.now()}`;
    const content = await this.readFile(filePath);
    
    if (content && (await this.writeFile(backupPath, content))) {
      return backupPath;
    }
    
    return null;
  }

  /**
   * Detect project framework and configuration
   */
  static async detectProject(rootDir: string): Promise<ProjectInfo> {
    const packageJsonPath = join(rootDir, 'package.json');
    const packageJson = await this.readFile(packageJsonPath);
    
    if (!packageJson) {
      return {
        framework: 'unknown',
        packageManager: 'npm',
        hasTypeScript: false,
        hasTailwind: false,
        hasESLint: false,
        hasPrettier: false,
        hasVitest: false,
        hasJest: false,
        hasPlaywright: false,
        hasCypress: false,
        rootDir,
        version: '1.0.0',
        name: 'unknown'
      };
    }

    const pkg = JSON.parse(packageJson);
    const dependencies = { ...pkg.dependencies, ...pkg.devDependencies };

    // Use enhanced framework detection
    const framework = await this.detectFramework(rootDir);

    // Detect package manager (npm is default)
    let packageManager: ProjectInfo['packageManager'] = 'npm';
    if (await this.fileExists(join(rootDir, 'yarn.lock'))) {
      packageManager = 'yarn';
    } else if (await this.fileExists(join(rootDir, 'pnpm-lock.yaml'))) {
      packageManager = 'pnpm';
    }

    return {
      framework,
      packageManager,
      hasTypeScript: dependencies.typescript || (await this.fileExists(join(rootDir, 'tsconfig.json'))),
      hasTailwind: dependencies.tailwindcss || (await this.fileExists(join(rootDir, 'tailwind.config.js'))),
      hasESLint: dependencies.eslint || (await this.fileExists(join(rootDir, '.eslintrc.js'))),
      hasPrettier: dependencies.prettier || (await this.fileExists(join(rootDir, '.prettierrc'))),
      hasVitest: dependencies.vitest || (await this.fileExists(join(rootDir, 'vitest.config.js'))),
      hasJest: dependencies.jest || (await this.fileExists(join(rootDir, 'jest.config.js'))),
      hasPlaywright: dependencies['@playwright/test'] || (await this.fileExists(join(rootDir, 'playwright.config.js'))),
      hasCypress: dependencies.cypress || (await this.fileExists(join(rootDir, 'cypress.config.js'))),
      rootDir,
      version: pkg.version || '1.0.0',
      name: pkg.name || 'unknown'
    };
  }

  /**
   * Check if project is using ESM
   */
  static async isESMProject(rootDir: string): Promise<boolean> {
    const packageJsonPath = join(rootDir, 'package.json');
    const packageJson = await this.readFile(packageJsonPath);
    
    if (!packageJson) return false;
    
    const pkg = JSON.parse(packageJson);
    
    // Check for explicit ESM declaration
    if (pkg.type === 'module') return true;
    
    // Check for .mjs files
    if (await this.fileExists(join(rootDir, 'index.mjs'))) return true;
    
    // Check for ESM imports in package.json
    if (pkg.exports && typeof pkg.exports === 'object') {
      return Object.values(pkg.exports).some((exp: any) => 
        exp.import || exp.default?.import
      );
    }
    
    return false;
  }

  /**
   * Get dynamic path mappings based on project structure and framework
   */
  static getDynamicPathMappings(projectInfo: ProjectInfo): Record<string, string[]> {
    const { framework, rootDir } = projectInfo;
    
    // Detect actual project structure
    const srcDir = this.detectSrcDirectory(rootDir);
    
    const baseMappings = {
      '@/*': [`./${srcDir}/*`],
    };
    
    // Framework-specific mappings
    const frameworkMappings = {
      nextjs: {
        '@/components/*': [`./${srcDir}/components/*`],
        '@/lib/*': [`./${srcDir}/lib/*`],
        '@/utils/*': [`./${srcDir}/utils/*`],
        '@/hooks/*': [`./${srcDir}/hooks/*`],
        '@/types/*': [`./${srcDir}/types/*`],
      },
      'react-vite': {
        '@/components/*': [`./${srcDir}/components/*`],
        '@/lib/*': [`./${srcDir}/lib/*`],
        '@/hooks/*': [`./${srcDir}/hooks/*`],
        '@/utils/*': [`./${srcDir}/utils/*`],
      },
      'react-cra': {
        '@/components/*': [`./${srcDir}/components/*`],
        '@/lib/*': [`./${srcDir}/lib/*`],
        '@/hooks/*': [`./${srcDir}/hooks/*`],
        '@/utils/*': [`./${srcDir}/utils/*`],
      },
      vue: {
        '@/components/*': [`./${srcDir}/components/*`],
        '@/composables/*': [`./${srcDir}/composables/*`],
        '@/utils/*': [`./${srcDir}/utils/*`],
        '@/stores/*': [`./${srcDir}/stores/*`],
      },
      angular: {
        '@/shared/*': [`./${srcDir}/shared/*`],
        '@/core/*': [`./${srcDir}/core/*`],
        '@/features/*': [`./${srcDir}/features/*`],
      },
      svelte: {
        '@/lib/*': [`./${srcDir}/lib/*`],
        '@/components/*': [`./${srcDir}/components/*`],
        '@/stores/*': [`./${srcDir}/stores/*`],
      },
      express: {
        '@/utils/*': [`./${srcDir}/utils/*`],
        '@/services/*': [`./${srcDir}/services/*`],
        '@/middleware/*': [`./${srcDir}/middleware/*`],
        '@/routes/*': [`./${srcDir}/routes/*`],
      },
      fastify: {
        '@/utils/*': [`./${srcDir}/utils/*`],
        '@/services/*': [`./${srcDir}/services/*`],
        '@/plugins/*': [`./${srcDir}/plugins/*`],
        '@/routes/*': [`./${srcDir}/routes/*`],
      },
      nodejs: {
        '@/utils/*': [`./${srcDir}/utils/*`],
        '@/lib/*': [`./${srcDir}/lib/*`],
        '@/types/*': [`./${srcDir}/types/*`],
      },
    };
    
    return {
      ...baseMappings,
      ...(frameworkMappings[framework as keyof typeof frameworkMappings] || {}),
    };
  }

  /**
   * Detect actual source directory structure
   */
  static async detectSrcDirectory(rootDir: string): Promise<string> {
    const possibleSrcDirs = ['src', 'app', 'lib', 'source'];
    
    for (const dir of possibleSrcDirs) {
      if (await this.fileExists(join(rootDir, dir))) {
        return dir;
      }
    }
    
    return 'src'; // Default
  }

  /**
   * Detect components directory
   */
  static async detectComponentsDirectory(rootDir: string, srcDir: string): Promise<string> {
    const possibleComponentDirs = [
      join(srcDir, 'components'),
      join(srcDir, 'app', 'components'),
      join(srcDir, 'shared', 'components'),
      'components'
    ];
    
    for (const dir of possibleComponentDirs) {
      if (await this.fileExists(join(rootDir, dir))) {
        return dir;
      }
    }
    
    return join(srcDir, 'components'); // Default
  }

  /**
   * Detect lib directory
   */
  static async detectLibDirectory(rootDir: string, srcDir: string): Promise<string> {
    const possibleLibDirs = [
      join(srcDir, 'lib'),
      join(srcDir, 'utils'),
      join(srcDir, 'shared'),
      'lib'
    ];
    
    for (const dir of possibleLibDirs) {
      if (await this.fileExists(join(rootDir, dir))) {
        return dir;
      }
    }
    
    return join(srcDir, 'lib'); // Default
  }

  /**
   * Get package.json dependencies
   */
  static async getDependencies(rootDir: string): Promise<Record<string, string>> {
    const packageJsonPath = join(rootDir, 'package.json');
    const packageJson = await this.readFile(packageJsonPath);
    
    if (!packageJson) {
      return {};
    }

    const pkg = JSON.parse(packageJson);
    return { ...pkg.dependencies, ...pkg.devDependencies };
  }

  /**
   * Add dependency to package.json
   */
  static async addDependency(rootDir: string, name: string, version: string, isDev = false): Promise<boolean> {
    const packageJsonPath = join(rootDir, 'package.json');
    const packageJson = await this.readFile(packageJsonPath);
    
    if (!packageJson) {
      return false;
    }

    const pkg = JSON.parse(packageJson);
    
    if (isDev) {
      if (!pkg.devDependencies) {
        pkg.devDependencies = {};
      }
      pkg.devDependencies[name] = version;
    } else {
      if (!pkg.dependencies) {
        pkg.dependencies = {};
      }
      pkg.dependencies[name] = version;
    }

    return await this.writeFile(packageJsonPath, JSON.stringify(pkg, null, 2));
  }

  /**
   * Add script to package.json
   */
  static async addScript(rootDir: string, name: string, command: string): Promise<boolean> {
    const packageJsonPath = join(rootDir, 'package.json');
    const packageJson = await this.readFile(packageJsonPath);
    
    if (!packageJson) {
      return false;
    }

    const pkg = JSON.parse(packageJson);
    
    if (!pkg.scripts) {
      pkg.scripts = {};
    }
    
    pkg.scripts[name] = command;

    return await this.writeFile(packageJsonPath, JSON.stringify(pkg, null, 2));
  }
}


